import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { ParticipantTypeEnum } from '@clubspark-node/clubspark-tennis-types';
import { DialogActions, DialogContent, Grid, MenuItem } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { participantRoles } from 'tods-competition-factory';
import { v4 as uuid } from 'uuid';

import StandardButton from 'components/buttons/StandardButton';
import { useStyles } from 'components/dialogs/addTeamDialog/styles';
import TMXDialog from 'components/dialogs/TMXDialog';
import FormErrorMessage from 'components/forms/FormErrorMessage';
import TMXInput from 'components/inputs/TMXInput';
import TMXSelect from 'components/selectors/tmxSelector/TMXSelect';
import { FormLabelText, H3 } from 'components/typography/Typography';
import { type ParticipantInterface } from 'typedefs/store/tmxTypes';

interface AddTeamProps {
  open?: boolean;
  loading?: boolean;
  onClose: () => void;
  onCloseActionsPopover?: () => void;
  validEvents: any[];
  onAddTeam: (team: ParticipantInterface, eventId: string, isEdit: boolean) => void;
  editMode: boolean;
  participant: ParticipantInterface;
}

const { COMPETITOR } = participantRoles;

const AddTeamDialog: React.FC<React.PropsWithChildren<AddTeamProps>> = ({
  open,
  loading,
  onClose,
  onCloseActionsPopover,
  validEvents,
  onAddTeam,
  editMode,
  participant,
}) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const classesProp = useMemo(() => ({ paper: classes.teamDialogPaper }), [classes.teamDialogPaper]);

  const [teamName, setTeamName] = useState<string>(editMode ? participant?.participantName : '');
  const [teamAbbreviation, setTeamAbbreviation] = useState<string>(editMode ? participant?.participantOtherName : '');
  const [chosenEvent, setChosenEvent] = useState<string>(editMode ? '' : 'noEvent');
  const [contactName, setContactName] = useState<string>(editMode ? participant?.contacts?.[0]?.name : '');
  const [contactEmail, setContactEmail] = useState<string>(editMode ? participant?.contacts?.[0]?.emailAddress : '');
  const [contactPhone, setContactPhone] = useState<string>(editMode ? participant?.contacts?.[0]?.telephone : '');
  const [showNameError, setShowNameError] = useState<boolean>(false);

  const disabledSave = !teamName?.trim();

  const handleTeamNameChange = useCallback((event) => {
    setShowNameError(!event.target.value.trim());
    setTeamName(event.target.value);
  }, []);

  const handleTeamAbbreviationChange = useCallback((event) => {
    setTeamAbbreviation(event.target.value);
  }, []);

  const handleEventChange = useCallback((event) => {
    setChosenEvent(event.target.value);
  }, []);

  const handleContactNameChange = useCallback((event) => {
    setContactName(event.target.value);
  }, []);

  const handleContactEmailChange = useCallback((event) => {
    setContactEmail(event.target.value);
  }, []);

  const handleContactPhoneChange = useCallback((event) => {
    setContactPhone(event.target.value);
  }, []);

  const resetValues = useCallback(() => {
    setTeamName(editMode ? participant?.participantName : '');
    setTeamAbbreviation(editMode ? participant?.participantOtherName : '');
    setChosenEvent(editMode ? '' : 'noEvent');
    setContactName(editMode ? participant?.contacts?.[0]?.name : '');
    setContactEmail(editMode ? participant?.contacts?.[0]?.emailAddress : '');
    setContactPhone(editMode ? participant?.contacts?.[0]?.telephone : '');
  }, [editMode, participant]);

  const addTeam = useCallback(() => {
    const newParticipant: ParticipantInterface = {
      participantId: editMode ? participant?.participantId : uuid().toUpperCase(),
      participantType: ParticipantTypeEnum.TEAM,
      participantRole: COMPETITOR,
      participantName: teamName,
      participantOtherName: teamAbbreviation,
      contacts: [
        {
          name: contactName,
          emailAddress: contactEmail,
          telephone: contactPhone,
        },
      ],
    };
    onAddTeam(newParticipant, chosenEvent, editMode);
    onClose();
    if (onCloseActionsPopover) {
      onCloseActionsPopover();
    }
  }, [
    editMode,
    participant?.participantId,
    teamName,
    teamAbbreviation,
    contactName,
    contactEmail,
    contactPhone,
    onAddTeam,
    chosenEvent,
    onClose,
    onCloseActionsPopover,
  ]);

  const handleOnClose = useCallback(() => {
    onClose();
    onCloseActionsPopover();
  }, [onClose, onCloseActionsPopover]);

  useEffect(() => {
    resetValues();
  }, [resetValues]);

  return (
    <TMXDialog fullWidth classes={classesProp} open={open} TransitionProps={{ onExited: resetValues }}>
      <H3>{editMode ? t('teams.editTeam') : t('teams.createTeam')}</H3>
      <DialogContent className={classes.dialogContent}>
        <Grid container>
          <Grid item lg={12} md={12} sm={12} xs={12}>
            <FormLabelText>{t('nm')}*</FormLabelText>
            <TMXInput fullWidth value={teamName} onChange={handleTeamNameChange} />
            {showNameError && <FormErrorMessage message={t('isRequired', { type: 'name' })} />}
          </Grid>
          <Grid item lg={12} md={12} sm={12} xs={12}>
            <FormLabelText>{t('Abbreviation')}</FormLabelText>
            <TMXInput fullWidth value={teamAbbreviation} onChange={handleTeamAbbreviationChange} />
          </Grid>
          {!editMode && (
            <Grid item sm={12} xs={12}>
              <FormLabelText>{t('ent')}</FormLabelText>
              <TMXSelect fullWidth value={chosenEvent} onChange={handleEventChange}>
                <MenuItem selected={true} value="noEvent">
                  {t('teams.defaultEventText')}
                </MenuItem>
                {validEvents.map((event) => (
                  <MenuItem key={event.eventId} value={event.eventId}>
                    {event.eventName}
                  </MenuItem>
                ))}
              </TMXSelect>
            </Grid>
          )}
          <Grid item lg={12} md={12} sm={12} xs={12}>
            <FormLabelText>{t('teams.contact')}</FormLabelText>
            <TMXInput fullWidth placeholder={t('nm')} value={contactName} onChange={handleContactNameChange} />
          </Grid>
          <Grid item lg={12} md={12} sm={12} xs={12}>
            <TMXInput
              fullWidth
              className={classes.dialogContentElement}
              placeholder={t('emailAddress')}
              value={contactEmail}
              onChange={handleContactEmailChange}
            />
          </Grid>
          <Grid item lg={12} md={12} sm={12} xs={12}>
            <TMXInput
              fullWidth
              className={classes.dialogContentElement}
              placeholder={t('phn')}
              value={contactPhone}
              onChange={handleContactPhoneChange}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions className={classes.actionsContainer}>
        <StandardButton onClick={handleOnClose}>{t('actions.cancel')}</StandardButton>
        <StandardButton disabled={disabledSave} loading={loading} variant="primary" onClick={addTeam}>
          {t('confirm')}
        </StandardButton>
      </DialogActions>
    </TMXDialog>
  );
};

export default AddTeamDialog;
